import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TermsContent } from "@/components/TermsContent";

export const TermsGateModal = () => {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [isAdult, setIsAdult] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!user || (user.terms_accepted && user.adult_confirmed)) return null;

  const accept = async () => {
    setSaving(true);
    try {
      await api.post("/me/accept-terms");
      await refresh();
      toast.success("Thank you. Welcome back to your practice.");
    } catch {
      toast.error("Could not record your agreement. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" data-testid="terms-gate-modal">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
        className="w-full max-w-2xl bg-canvas rounded-3xl border border-line flex flex-col max-h-[90vh] overflow-hidden"
      >
        <div className="p-6 sm:p-8 border-b border-line">
          <div className="flex items-center gap-2 mb-3">
            <Sparkle size={16} weight="fill" className="text-clay" />
            <span className="text-xs uppercase tracking-[0.3em] text-ink-mute">One thing before you continue</span>
          </div>
          <h2 className="font-serif-display text-2xl sm:text-3xl text-ink leading-tight">
            Our user agreement, in full.
          </h2>
          <p className="text-sm text-ink-soft mt-2 leading-relaxed">
            Clarity Mirror is for adults (18+), and your account predates our explicit agreement step. For full transparency, here is everything. Please read, confirm, and agree to keep writing.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 sm:p-8" data-testid="terms-gate-content">
          <TermsContent />
        </div>

        <div className="p-6 sm:p-8 border-t border-line bg-canvas-warm/50">
          <div className="space-y-3 mb-5">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isAdult}
                onChange={(e) => setIsAdult(e.target.checked)}
                data-testid="terms-gate-adult-checkbox"
                className="mt-0.5 h-4 w-4 rounded border-line-strong accent-[#B0745B] cursor-pointer"
              />
              <span className="text-sm text-ink-soft leading-relaxed">
                I confirm that I am <b>18 years of age or older</b>.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                data-testid="terms-gate-checkbox"
                className="mt-0.5 h-4 w-4 rounded border-line-strong accent-[#B0745B] cursor-pointer"
              />
              <span className="text-sm text-ink-soft leading-relaxed">
                I have read and agree to the user agreement &amp; privacy terms above and the{" "}
                <a href="/terms-of-service" target="_blank" rel="noreferrer" data-testid="terms-gate-tos-link" className="text-clay hover:text-clay-dark underline underline-offset-4">
                  Terms of Service
                </a>
                .
              </span>
            </label>
          </div>
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={accept}
              disabled={!agreed || !isAdult || saving}
              data-testid="terms-gate-agree-btn"
              className="rounded-full bg-ink text-canvas px-7 py-3 text-sm font-medium hover:bg-ink/90 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? "One moment…" : "I agree, continue"}
            </button>
            <button
              onClick={() => { logout(); nav("/"); }}
              data-testid="terms-gate-signout"
              className="text-sm text-ink-mute hover:text-ink underline underline-offset-4"
            >
              Not now, sign me out
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
